import { GAME_CONFIG, type AnimeRank, type GameConfig } from './config';
import { RARITIES, type Rarity } from './rarity';

export function computeRarity(favourites: number, config: GameConfig = GAME_CONFIG): Rarity {
  let result: Rarity = 'common';
  for (const rarity of RARITIES) {
    if (favourites >= config.rarities[rarity].minFavourites) result = rarity;
  }
  return result;
}


export const ANIME_RANK_TAIL_SHARE = 0.15;


export function animeRankTailCount(characterCount: number): number {
  if (!Number.isFinite(characterCount) || characterCount <= 0) return 0;
  return Math.min(characterCount, Math.max(1, Math.round(characterCount * ANIME_RANK_TAIL_SHARE)));
}

function mean(values: readonly number[]): number {
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}


export function animeExtremeAverage(favourites: readonly number[]): number {
  const values = favourites.filter((value) => Number.isFinite(value)).map((value) => Math.max(0, value));
  const tail = animeRankTailCount(values.length);
  if (tail === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const worst = mean(sorted.slice(0, tail));
  const best = mean(sorted.slice(sorted.length - tail));
  return (best + worst) / 2;
}


export function computeAnimeRank(favourites: readonly number[], config: GameConfig = GAME_CONFIG): AnimeRank {
  const average = animeExtremeAverage(favourites);
  let result: AnimeRank = 'F';
  for (const { rank, minFavourites } of config.animeRanks) {
    if (average >= minFavourites) result = rank;
  }
  return result;
}


export function animeRankCaseSql(averageSql: string): string {
  const clauses = [...GAME_CONFIG.animeRanks]
    .filter((entry) => entry.minFavourites > 0)
    .reverse()
    .map((entry) => `WHEN ${averageSql} >= ${entry.minFavourites} THEN '${entry.rank}'`);
  return `CASE ${clauses.join(' ')} ELSE 'F' END`;
}


export function animeRankUpdateSql(idPlaceholders: string): string {
  return `WITH scored AS (
    SELECT anime_id,
           (AVG(CASE WHEN best_rank <= tail THEN favourites END)
            + AVG(CASE WHEN worst_rank <= tail THEN favourites END)) / 2.0 AS average
    FROM (
      SELECT anime_id,
             favourites,
             ROW_NUMBER() OVER (PARTITION BY anime_id ORDER BY favourites DESC, id ASC) AS best_rank,
             ROW_NUMBER() OVER (PARTITION BY anime_id ORDER BY favourites ASC, id ASC) AS worst_rank,
             MIN(
               COUNT(*) OVER (PARTITION BY anime_id),
               MAX(1, CAST(ROUND(COUNT(*) OVER (PARTITION BY anime_id) * ${ANIME_RANK_TAIL_SHARE}) AS INTEGER))
             ) AS tail
      FROM Character_List
      WHERE is_obtainable = 1 AND anime_id IN (${idPlaceholders})
    )
    GROUP BY anime_id
  )
  UPDATE Anime_List
  SET rank = COALESCE(
    (SELECT ${animeRankCaseSql('average')} FROM scored WHERE scored.anime_id = Anime_List.id),
    'F'
  )
  WHERE id IN (${idPlaceholders})
    AND anilist_id IS NOT NULL
    AND NOT EXISTS (
      SELECT 1 FROM Catalog_Overrides o
      WHERE o.entity_type = 'anime' AND o.entity_id = Anime_List.id AND o.field = 'rank'
    )`;
}


export function computeCharacterValue(
  favourites: number,
  rarity: Rarity,
  config: GameConfig = GAME_CONFIG,
): number {
  return config.rarities[rarity].baseValue + Math.floor(Math.sqrt(Math.max(0, favourites)));
}


export function effectiveCardValue(baseValue: number, upgradeLevel: number): number {
  const level = Math.max(1, Math.floor(upgradeLevel));
  const base = Math.max(0, Math.floor(baseValue));
  return base * (level <= 5 ? 2 ** (level - 1) : 16 + (level - 5) * 4);
}
